import React from "react";
import { defaultEditorDefns, EditorDefns, TlfViewModeConfig } from "../../utility";
import { ArsTableRow } from "../table";
import { ArsListingContent } from "./ArsListingContent";
import { ArsListingContextProvider } from "./ArsListingContextProvider";
import { type ArsListingBody } from "./interface";

/**
 * Props for the ArsListing component.
 */
export interface ArsListingProps<T> {
  /** ID for the listing component */
  id?: string;
  /** Pre-populated listing body shown on first render. */
  arsListingBody: ArsListingBody<T>;
  /** Calculated scaffold listing rows */
  calculatedTableRows: ArsTableRow<T>[];
  /** Optional flag to disable listing interactions. */
  disabled?: boolean;
  /** Editor props used by cell editors */
  editorProps?: EditorDefns<T>;
  /** Horizontal justification of the scaffold table within its container */
  justification?: "left" | "center" | "right";
  /** Optional CSS styles for the sidebar container */
  sidebarStyle?: React.CSSProperties;
  /** Optional sidebar width. */
  sidebarWidth?: string;
  /** Container style for the listing component */
  style?: React.CSSProperties;
  /** TLF view mode controlling how cell data is displayed. */
  tlfViewMode: TlfViewModeConfig;
  /** Callback invoked when the listing body changes */
  updateArsListingBody: (newBody: ArsListingBody<T>) => void;
}

/**
 * ArsListing presents a scaffold table with a variable sidebar. Users can drag one or
 * more variables onto header cells and position them before, after, or in place using
 * nine recognised drop zones (edges, corners, and centre).
 *
 * @param props - Component props
 * @param props.id - ID for the listing component
 * @param props.arsListingBody - Listing body data
 * @param props.calculatedTableRows - Calculated scaffold table rows
 * @param props.disabled - Optional flag that disables listing interactions
 * @param props.editorProps - Editor props used by cell editors
 * @param props.justification - Horizontal justification of the scaffold table within its container
 * @param props.sidebarWidth - Optional sidebar width
 * @param props.tlfViewMode - TLF view mode controlling how cell data is displayed
 * @param props.sidebarStyle - Optional CSS styles for the sidebar container
 * @param props.style - Container style for the listing component
 * @param props.updateArsListingBody - Callback invoked when the listing body changes
 * @returns ArsListing table and draggable variable sidebar
 */
export const ArsListing = <T,>({
  id = "ars-listing",
  arsListingBody,
  calculatedTableRows,
  disabled = false,
  editorProps = defaultEditorDefns as EditorDefns<T>,
  justification,
  sidebarWidth = "360px",
  tlfViewMode,
  updateArsListingBody,
  sidebarStyle,
  style,
}: ArsListingProps<T>): React.ReactElement => {
  return (
    <ArsListingContextProvider
      bodyData={arsListingBody}
      calculatedTableRows={calculatedTableRows}
      updateBodyData={updateArsListingBody}
      settings={{
        id,
        disabled,
        editorProps,
        justification,
        sidebarStyle,
        sidebarWidth,
        style,
        tlfViewMode,
        variableFilter: "",
      }}
    >
      <ArsListingContent />
    </ArsListingContextProvider>
  );
};

ArsListing.displayName = "ArsListing";
